import { Checkbox } from '@creditornot/cb-checkbox';
import { FormRow, InputContainer } from '@creditornot/cb-form';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { Input } from '@creditornot/cb-input';
import { Radio } from '@creditornot/cb-radio';
import isEqual from 'lodash/isEqual';
import React from 'react';
import { Field, useForm, useField } from 'react-final-form';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { RFFFieldErrorWrapper } from 'common/components';
import { useSelectionInfoContext } from 'modules/shared-menu/contexts/SelectionInfoContext';

import { OptionValuePartialForm } from './OptionValuePartialForm';
import { FormOptionValue, OptionFormData } from './types';
import { SectionTitle } from '../CommonPanelStyles';

type Props = {
  allowChangingOptionType: boolean;
  hideSwitchOptionType?: boolean;
};

const Root = styled.div`
  display: flex;
  flex-direction: column;
`;

const Section = styled.div`
  & + & {
    border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
  }
`;

const RadioButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledRadioButton = styled(Radio)`
  & + & {
    margin-block-start: 12px;
  }
`;

const StyledInputContainer = styled(InputContainer)`
  max-width: 392px;
`;

const ValueForm = styled(OptionValuePartialForm)`
  margin-block-start: 12px;
`;

const deleteOptionValue = (
  fields: FieldArrayRenderProps<FormOptionValue[], HTMLElement>['fields'],
  index: number,
  defaultValueIndex: number,
  change: (name: string, value?: any) => void,
) => {
  if (index === defaultValueIndex) {
    change('defaultValueIndex', 0);
  } else if (index < defaultValueIndex) {
    change('defaultValueIndex', defaultValueIndex - 1);
  } else {
    // Current `defaultValueIndex` was less than deleted index. This is a no-op.
  }

  fields.remove(index);
};

export const OptionFormFields = ({ allowChangingOptionType, hideSwitchOptionType = false }: Props) => {
  const { country } = useSelectionInfoContext();
  const intl = useIntl();

  const form = useForm<OptionFormData>();
  const {
    input: { value: optionType },
  } = useField<OptionFormData['type']>('type');
  const defaultValueIndex = useField<OptionFormData['defaultValueIndex']>('defaultValueIndex').input.value;

  const isChoice = ['choice', 'multi_choice'].includes(optionType);
  const fieldsName = isChoice ? 'values' : 'boolValues';

  // The "Switch" ("bool") option type is deprecated, but it's possible to have old existing options with the type
  // Only show the "Switch" toggle when in edit mode & the option is actually a switch
  const showSwitchType = !hideSwitchOptionType && !isChoice;

  return (
    <Root>
      <SectionTitle>
        <FormattedMessage id="menu-option.details-header" />
      </SectionTitle>
      <Section>
        <FormRow label={intl.formatMessage({ id: 'menu-option.option-type-label' })}>
          <RadioButtonsContainer>
            <Field name="type" type="radio" value="choice">
              {field => (
                <StyledRadioButton {...field.input} disabled={!allowChangingOptionType}>
                  <FormattedMessage id="menu-option.single-choice" />
                </StyledRadioButton>
              )}
            </Field>
            <Field name="type" type="radio" value="multi_choice">
              {field => (
                <StyledRadioButton {...field.input} disabled={!allowChangingOptionType}>
                  <FormattedMessage id="menu-option.multi-choice" />
                </StyledRadioButton>
              )}
            </Field>
            {showSwitchType && (
              <Field name="type" type="radio" value="bool">
                {field => (
                  <StyledRadioButton {...field.input} disabled={!allowChangingOptionType}>
                    <FormattedMessage id="menu-option.option-type-bool" />
                  </StyledRadioButton>
                )}
              </Field>
            )}
          </RadioButtonsContainer>
        </FormRow>
        <FormRow
          label={intl.formatMessage({
            id: isChoice ? 'menu-option.choice-title-label' : 'menu-option.bool-title-label',
          })}
          info={intl.formatMessage({ id: 'menu-option.title-example' })}
        >
          <Field name="name">
            {field => (
              <RFFFieldErrorWrapper field={field}>
                {input => (
                  <StyledInputContainer characterCount={{ value: field.input.value.length, max: 50 }}>
                    <Input
                      {...input}
                      placeholder={intl.formatMessage({ id: 'menu-option.title-placeholder' })}
                      maxLength={50}
                    />
                  </StyledInputContainer>
                )}
              </RFFFieldErrorWrapper>
            )}
          </Field>
        </FormRow>
        <FormRow label={intl.formatMessage({ id: 'menu-option.show-to-courier' })}>
          <Field name="visibleToCourier">
            {field => <Checkbox checked={field.input.value} onChange={field.input.onChange} />}
          </Field>
        </FormRow>
        <FormRow label={intl.formatMessage({ id: 'menu.pos-id' })}>
          <Field name="externalId">
            {field => (
              <RFFFieldErrorWrapper field={field}>
                {input => (
                  <StyledInputContainer>
                    <Input {...input} placeholder={intl.formatMessage({ id: 'menu.pos-id-placeholder' })} />
                  </StyledInputContainer>
                )}
              </RFFFieldErrorWrapper>
            )}
          </Field>
        </FormRow>
      </Section>
      <Section>
        <SectionTitle>
          <FormattedMessage id="menu-option.choices-header" />
        </SectionTitle>
        <FieldArray
          name={fieldsName}
          // The `key` prop is required to work around a bug in react-final-form-arrays:
          // https://github.com/final-form/react-final-form-arrays/issues/125
          key={fieldsName}
          isEqual={isEqual}
        >
          {({ fields }) => {
            const move = (fromIdx: number, toIdx: number) => {
              if (fromIdx === toIdx) {
                return;
              }
              if (fromIdx === defaultValueIndex) {
                form.change('defaultValueIndex', toIdx);
              }
              if (toIdx === defaultValueIndex) {
                form.change('defaultValueIndex', fromIdx);
              }

              fields.move(fromIdx, toIdx);
            };

            return (
              <div>
                {fields.map((name, index) => (
                  <ValueForm
                    country={country}
                    fields={fields}
                    index={index}
                    isChoice={isChoice}
                    isMultiChoice={optionType === 'multi_choice'}
                    key={fields.value[index].id}
                    name={name}
                    onMoveUp={() => move(index, Math.max(0, index - 1))}
                    onMoveDown={() => move(index, Math.min(index + 1, fields.length! - 1))}
                    onDelete={() => {
                      deleteOptionValue(fields, index, defaultValueIndex, form.change);
                    }}
                  />
                ))}
              </div>
            );
          }}
        </FieldArray>
      </Section>
    </Root>
  );
};
